/**
 * Field Name Mapping
 * 
 * Maps between API field names (snake_case) and form field names (camelCase).
 * This provides a centralized place for field name translation.
 */

/**
 * Mapping from API field names to React Hook Form field paths
 */
export const API_TO_FORM_FIELD_MAP: Record<string, string> = {
  // Patient Information (Box 2, 3, 5)
  'patient_first_name': 'patientName.firstName',
  'patient_last_name': 'patientName.lastName',
  'patient_middle_initial': 'patientName.middleInitial',
  'patient_dob': 'patientBirthDate',
  'patient_sex': 'patientSex',
  
  // Patient Address (Box 5)
  'patient_address.line1': 'patientAddress.line1',
  'patient_address.line2': 'patientAddress.line2',
  'patient_address.city': 'patientAddress.city',
  'patient_address.state': 'patientAddress.state',
  'patient_address.zip': 'patientAddress.zip',
  'patient_address.phone': 'patientAddress.phone',
  
  // Primary Insured (Box 1a, 4, 7)
  'primary_insured_id_number': 'insuredIdNumber',
  'primary_insured_first_name': 'insuredName.firstName',
  'primary_insured_last_name': 'insuredName.lastName',
  'primary_insured_middle_initial': 'insuredName.middleInitial',
  'primary_insured_dob': 'insuredDateOfBirth',
  'primary_insured_sex': 'insuredSex',
  
  // Primary Insured Address (Box 7)
  'primary_insured_address.line1': 'insuredAddress.line1',
  'primary_insured_address.line2': 'insuredAddress.line2',
  'primary_insured_address.city': 'insuredAddress.city',
  'primary_insured_address.state': 'insuredAddress.state',
  'primary_insured_address.zip': 'insuredAddress.zip',
  'primary_insured_address.phone': 'insuredAddress.phone',
  
  // Insurance Information (Box 1, 11)
  'insurance_type': 'insuranceType',
  'primary_insured_policy_group_number': 'insuredPolicyGroup',
  'insurance_plan_name': 'insuredPlanName',
  
  // Other Insured (Box 9)
  'other_insured_first_name': 'otherInsuredName.firstName',
  'other_insured_last_name': 'otherInsuredName.lastName',
  'other_insured_middle_initial': 'otherInsuredName.middleInitial',
  'other_insured_policy_group_number': 'otherInsuredPolicyGroup',
  'other_insured_plan_name': 'otherInsuredPlanName',
  
  // Provider Information (Box 17, 31, 32, 33)
  'referring_provider_name': 'referringProviderName',
  'referring_provider_npi': 'referringProviderNPI',
  'provider_npi': 'billingProvider.npi',
  'provider_name': 'billingProvider.name',
  'tax_id': 'federalTaxIdNumber',
  'tax_id_type': 'taxIdType',
  
  // Service Facility (Box 32)
  'service_facility_name': 'serviceFacility.name',
  'service_facility_npi': 'serviceFacility.npi',
  'service_facility_address.line1': 'serviceFacility.address',
  'service_facility_address.line2': 'serviceFacility.address2',
  'service_facility_address.city': 'serviceFacility.city',
  'service_facility_address.state': 'serviceFacility.state',
  'service_facility_address.zip': 'serviceFacility.zip',
  
  // Billing Provider (Box 33)
  'billing_address.line1': 'billingProvider.address',
  'billing_address.line2': 'billingProvider.address2',
  'billing_address.city': 'billingProvider.city',
  'billing_address.state': 'billingProvider.state',
  'billing_address.zip': 'billingProvider.zip',
  'billing_phone': 'billingProvider.phone',
  
  // Diagnosis (Box 21)
  'diagnoses': 'diagnosisCodes.A', // Map to first diagnosis field
  // Map diagnosis array indices to letter-based fields
  'diagnoses.0': 'diagnosisCodes.A',
  'diagnoses.1': 'diagnosisCodes.B',
  'diagnoses.2': 'diagnosisCodes.C',
  'diagnoses.3': 'diagnosisCodes.D',
  'diagnoses.4': 'diagnosisCodes.E',
  'diagnoses.5': 'diagnosisCodes.F',
  'diagnoses.6': 'diagnosisCodes.G',
  'diagnoses.7': 'diagnosisCodes.H',
  'diagnoses.8': 'diagnosisCodes.I',
  'diagnoses.9': 'diagnosisCodes.J',
  'diagnoses.10': 'diagnosisCodes.K',
  'diagnoses.11': 'diagnosisCodes.L',
  // Also keep direct mappings for form fields
  'diagnosisCodes.A': 'diagnosisCodes.A',
  'diagnosisCodes.B': 'diagnosisCodes.B',
  'diagnosisCodes.C': 'diagnosisCodes.C',
  'diagnosisCodes.D': 'diagnosisCodes.D',
  'diagnosisCodes.E': 'diagnosisCodes.E',
  'diagnosisCodes.F': 'diagnosisCodes.F',
  'diagnosisCodes.G': 'diagnosisCodes.G',
  'diagnosisCodes.H': 'diagnosisCodes.H',
  'diagnosisCodes.I': 'diagnosisCodes.I',
  'diagnosisCodes.J': 'diagnosisCodes.J',
  'diagnosisCodes.K': 'diagnosisCodes.K',
  'diagnosisCodes.L': 'diagnosisCodes.L',
  'icd_indicator': 'icdIndicator',
  
  // Dates (Box 14, 15, 16, 18)
  'current_illness_date': 'currentIllnessDate',
  'unable_to_work_from': 'unableToWorkFrom',
  'unable_to_work_to': 'unableToWorkTo',
  'hospitalization_from': 'hospitalizationFrom',
  'hospitalization_to': 'hospitalizationTo',
  
  // Claim Information (Box 19, 20, 22, 23)
  'additional_claim_info': 'additionalClaimInfo',
  'outside_lab': 'outsideLab',
  'outside_lab_charges_cents': 'outsideLabCharges',
  'resubmission_code': 'resubmissionCode',
  'original_ref_number': 'originalRefNo',
  'prior_authorization_number': 'priorAuthNumber',
  
  // Payment Information (Box 26, 27, 28, 29)
  'patient_account_number': 'patientAccountNo',
  'accept_assignment': 'acceptAssignment',
  'total_charge_cents': 'totalCharge',
  'amount_paid_cents': 'amountPaid',
  
  // Signatures (Box 12, 13, 31)
  'patient_signature': 'patientSignature.signed',
  'patient_signature_date': 'patientSignature.date',
  'patient_consent_on_file': 'patientSignature.signed',
  'insured_signature': 'insuredSignature.signed',
  'insured_signature_date': 'insuredSignature.date',
  'physician_signature': 'physicianSignature.signed',
  'physician_signature_date': 'physicianSignature.date',
  'provider_signature_on_file': 'physicianSignature.signed',
  
  // Relationship (Box 6)
  'patient_relationship_to_insured': 'patientRelationship',
  'primary_insured_relationship': 'patientRelationship',
  
  // Condition Related (Box 10)
  'condition_employment': 'conditionRelated.employment',
  'condition_auto_accident': 'conditionRelated.autoAccident',
  'condition_auto_accident_state': 'conditionRelated.autoAccidentState',
  'condition_other_accident': 'conditionRelated.otherAccident',
  
  // Service Lines / Claim Lines (Box 24)
  'claim_lines': 'serviceLines', // Map general claim_lines error to service lines array
  
  // Claim Line Fields (Box 24) - These are within claim_lines array
  'cpt_code': 'cptHcpcs',
  'date_from': 'dateFrom',
  'date_to': 'dateTo',
  'pos_code': 'placeOfService',
  'modifiers': 'modifiers',
  'diagnosis_pointers': 'diagnosisPointer',
  'charge_cents': 'charges',
  'units': 'units',
  'rendering_npi': 'renderingProviderId',
};

/**
 * Get the form field name for an API field
 * @param apiField - The API field name (e.g., "patient_dob")
 * @returns The form field name (e.g., "patientBirthDate") or the original if no mapping exists
 */
export function getFormFieldName(apiField: string): string {
  return API_TO_FORM_FIELD_MAP[apiField] || apiField;
}

/**
 * Create reverse mapping for form to API field names
 */
export const FORM_TO_API_FIELD_MAP: Record<string, string> = Object.entries(
  API_TO_FORM_FIELD_MAP
).reduce<Record<string, string>>((acc, [api, form]) => {
  acc[form] = api;
  return acc;
}, {});

/**
 * Get the API field name for a form field
 * @param formField - The form field name (e.g., "patientBirthDate")
 * @returns The API field name (e.g., "patient_dob") or the original if no mapping exists
 */
export function getApiFieldName(formField: string): string {
  return FORM_TO_API_FIELD_MAP[formField] || formField;
}

/**
 * Mapping from API field names to CMS box numbers
 * Used as fallback when API doesn't provide cms_box value
 */
export const FIELD_TO_BOX_MAP: Record<string, string> = {
  // Box 1 - Insurance Type
  'insurance_type': '1',
  
  // Box 1a - Insured ID
  'primary_insured_id_number': '1a',
  
  // Box 2 - Patient Name
  'patient_first_name': '2',
  'patient_last_name': '2',
  'patient_middle_initial': '2',
  
  // Box 3 - Patient Birth/Sex
  'patient_dob': '3',
  'patient_sex': '3',
  
  // Box 4 - Insured Name
  'primary_insured_first_name': '4',
  'primary_insured_last_name': '4',
  'primary_insured_middle_initial': '4',
  
  // Box 5 - Patient Address
  'patient_address.line1': '5',
  'patient_address.line2': '5',
  'patient_address.city': '5',
  'patient_address.state': '5',
  'patient_address.zip': '5',
  'patient_address.phone': '5',
  
  // Box 6 - Patient Relationship
  'patient_relationship_to_insured': '6',
  'primary_insured_relationship': '6',
  
  // Box 7 - Insured Address
  'primary_insured_address.line1': '7',
  'primary_insured_address.line2': '7',
  'primary_insured_address.city': '7',
  'primary_insured_address.state': '7',
  'primary_insured_address.zip': '7',
  'primary_insured_address.phone': '7',
  'primary_insured_dob': '7',
  'primary_insured_sex': '7',
  
  // Box 9 - Other Insured
  'other_insured_first_name': '9',
  'other_insured_last_name': '9',
  'other_insured_middle_initial': '9',
  'other_insured_policy_group_number': '9',
  'other_insured_plan_name': '9',
  
  // Box 10 - Patient Condition
  'condition_employment': '10',
  'condition_auto_accident': '10',
  'condition_auto_accident_state': '10',
  'condition_other_accident': '10',
  
  // Box 11 - Insured Policy
  'primary_insured_policy_group_number': '11',
  'insurance_plan_name': '11',
  
  // Box 12 - Patient Signature
  'patient_signature': '12',
  'patient_signature_date': '12',
  'patient_consent_on_file': '12',
  
  // Box 13 - Insured Signature
  'insured_signature': '13',
  'insured_signature_date': '13',
  
  // Box 14 - Current Illness Date
  'current_illness_date': '14',
  
  // Box 15 - Other Date
  'other_date': '15',
  
  // Box 16 - Unable to Work
  'unable_to_work_from': '16',
  'unable_to_work_to': '16',
  
  // Box 17 - Referring Provider
  'referring_provider_name': '17',
  'referring_provider_npi': '17',
  
  // Box 18 - Hospitalization
  'hospitalization_from': '18',
  'hospitalization_to': '18',
  
  // Box 19 - Additional Claim Info
  'additional_claim_info': '19',
  
  // Box 20 - Outside Lab
  'outside_lab': '20',
  'outside_lab_charges_cents': '20',
  
  // Box 21 - Diagnosis
  'diagnoses': '21',
  'diagnoses.0': '21',
  'diagnoses.1': '21',
  'diagnoses.2': '21',
  'diagnoses.3': '21',
  'diagnoses.4': '21',
  'diagnoses.5': '21',
  'diagnoses.6': '21',
  'diagnoses.7': '21',
  'diagnoses.8': '21',
  'diagnoses.9': '21',
  'diagnoses.10': '21',
  'diagnoses.11': '21',
  'icd_indicator': '21',
  
  // Box 22 - Resubmission
  'resubmission_code': '22',
  'original_ref_number': '22',
  
  // Box 23 - Prior Auth
  'prior_authorization_number': '23',
  
  // Box 24 - Service Lines
  'claim_lines': '24',
  
  // Box 25 - Federal Tax ID
  'tax_id': '25',
  'tax_id_type': '25',
  
  // Box 26 - Patient Account Number
  'patient_account_number': '26',
  
  // Box 27 - Accept Assignment
  'accept_assignment': '27',
  
  // Box 28 - Total Charge
  'total_charge_cents': '28',
  
  // Box 29 - Amount Paid
  'amount_paid_cents': '29',
  
  // Box 31 - Provider Signature
  'physician_signature': '31',
  'physician_signature_date': '31',
  'provider_signature_on_file': '31',
  
  // Box 32 - Service Facility
  'service_facility_name': '32',
  'service_facility_npi': '32',
  'service_facility_address.line1': '32',
  'service_facility_address.line2': '32',
  'service_facility_address.city': '32',
  'service_facility_address.state': '32',
  'service_facility_address.zip': '32',
  
  // Box 33 - Billing Provider
  'provider_npi': '33',
  'provider_name': '33',
  'billing_address.line1': '33',
  'billing_address.line2': '33',
  'billing_address.city': '33',
  'billing_address.state': '33',
  'billing_address.zip': '33',
  'billing_phone': '33',
};

/**
 * Get the CMS box number for an API field
 * @param apiField - The API field name (e.g., "patient_dob")
 * @returns The CMS box number (e.g., "3") or undefined if no mapping exists
 */
export function getFieldBox(apiField: string): string | undefined {
  // Check for claim line fields (they belong to box 24)
  if (apiField.startsWith('claim_lines.')) {
    return '24';
  }
  
  return FIELD_TO_BOX_MAP[apiField];
}